import type {
  ComponentPropsWithoutRef,
  ComponentType,
  ReactNode} from 'react';
import {
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';

import classNames from 'classnames';

import { cn } from '../../../utils';
import { GridTableRowProvider, useGridTableRow } from './GridTableRowProvider';

export type GridTableData = {
  [key: string]: any;
};

export interface RowRenderProps<Data extends GridTableData, Meta = undefined> {
  row: Data;
  rowIndex: number;
  colIndex: number;
  meta?: Meta;
  column: GridTableColumn<GridTableData, Meta>;
}

export interface GridTableColumn<Data extends GridTableData, Meta = undefined> {
  name: keyof Data | string;
  label: ReactNode;
  cellClassName?: string;
  headCellClassName?: string;
  bodyCellClassName?: string;
  hidden?: (params: { meta?: Meta }) => boolean;
  render?: (params: RowRenderProps<Data, Meta>) => ReactNode;
  meta?: Meta;
  Component?: ComponentType<RowRenderProps<Data, Meta>>;
  additionalContent?: (params: RowRenderProps<Data, Meta>) => ReactNode;
}

interface ContextValue<Data extends GridTableData = GridTableData, Meta = undefined> {
  columns: GridTableColumn<Data, Meta>[];
  data?: GridTableData[];
  rowId?: keyof Data | ((params: { row: GridTableData }) => number | string);
  rowClassName?: string;
  cellClassName?: string | ((params: { rowIndex: number; colIndex: number }) => string | undefined);
  bodyRowClassName?: string | ((params: { row: Data; rowIndex: number }) => string | undefined);
  headRowClassName?: string;
  bodyCellClassName?: string;
  headCellClassName?: string;
  meta?: Meta | any;
  HeadRowComponent?: ComponentType<{ className?: string }> | null;
  BodyRowComponent?: ComponentType<{ className?: string; onClick?: () => void }>;
}

const Context = createContext<ContextValue | null>(null);

export const useGridTable = () => {
  const value = useContext(Context);

  if (!value) {
    throw new Error(`No value provided for context Grid Table`);
  }

  return value as ContextValue;
};

export type GridTableProps<
  Data extends GridTableData,
  Meta = undefined,
> = ComponentPropsWithoutRef<'div'> &
  ContextValue<Data, Meta> & {
    onClickRow?: (row: Data) => void;
  };

export const GridTableBodyCell = ({
  className,
  rowIndex,
  colIndex,
  ...props
}: ComponentPropsWithoutRef<'div'> & { rowIndex: number; colIndex: number }) => {
  const { bodyCellClassName, cellClassName } = useGridTable();

  return (
    <div
      className={classNames(
        'grid-table-cell',
        typeof cellClassName === 'function' ? cellClassName({ rowIndex, colIndex }) : cellClassName,
        bodyCellClassName,
        className,
      )}
    >
      {props.children}
    </div>
  );
};

export const GridTableBodyRow = ({ className, ...props }: ComponentPropsWithoutRef<'div'>) => {
  const { rowClassName, bodyRowClassName } = useGridTable();
  const { row, rowIndex } = useGridTableRow();

  return (
    <div
      className={classNames(
        'grid-table-row',
        rowClassName,
        typeof bodyRowClassName === 'function'
          ? bodyRowClassName({ row, rowIndex })
          : bodyRowClassName,
        className,
      )}
      {...props}
    />
  );
};

export const GridTableBodyRowCells = () => {
  const { columns, meta } = useGridTable();
  const { row, rowIndex } = useGridTableRow();

  return (
    <>
      {columns.map((column, colIndex) => {
        const { name, Component, bodyCellClassName, cellClassName, render, hidden } = column;

        if (hidden && hidden({ meta })) {
          return null;
        }

        const renderProps: RowRenderProps<typeof row, typeof meta> = {
          column,
          rowIndex,
          colIndex,
          row,
          meta,
        };

        return (
          <GridTableBodyCell
            rowIndex={rowIndex}
            colIndex={colIndex}
            className={classNames(cellClassName, bodyCellClassName)}
            key={name}
          >
            {Component ? <Component {...renderProps} /> : render ? render(renderProps) : row[name]}
          </GridTableBodyCell>
        );
      })}
    </>
  );
};

const BaseGridTableBodyRow = ({ ...props }: ComponentPropsWithoutRef<'div'>) => {
  const [isOpen, setIsOpen] = useState(false);
  const { columns } = useGridTable();
  const { row } = useGridTableRow();

  const hasAdditionalContent = columns.some((column) => column.additionalContent);

  return (
    <>
      <GridTableBodyRow
        className={cn({
          '!shadow-none': isOpen,
          'flex flex-col': hasAdditionalContent,
        })}
        {...props}
      >
        <GridTableBodyRowCells />
        {hasAdditionalContent && (
          <GridTableBodyCell
            colIndex={columns.length}
            rowIndex={0}
            className="flex justify-start md-msq:justify-end mb-4 md-msq:mb-0"
          >
            <button
              onClick={() => setIsOpen((prev) => !prev)}
              className="text-base-text-pink text-caption-sm-a focus:outline-none"
            >
              {isOpen ? 'Hide details' : 'View details'}
            </button>
          </GridTableBodyCell>
        )}
      </GridTableBodyRow>
      {hasAdditionalContent && (
        <div
          className={classNames(
            //TODO: remove max-h
            'overflow-hidden',
            {
              'transition-[max-height] duration-700 ease-in max-h-96': isOpen,
              'transition-[max-height] duration-300 ease-out max-h-0': !isOpen,
            },
          )}
        >
          {columns.map((column, colIndex) => {
            const additionalContent = column.additionalContent?.({
              row,
              rowIndex: 0,
              colIndex,
              column,
            });

            return (
              additionalContent && (
                <div key={colIndex} className="justify-start bg-base-bg-secondary rounded-xl">
                  {additionalContent}
                </div>
              )
            );
          })}
        </div>
      )}
    </>
  );
};

export const BaseGridTableHeadRow = ({ className, ...props }: ComponentPropsWithoutRef<'div'>) => {
  const { rowClassName, columns, meta, cellClassName, headCellClassName, headRowClassName } =
    useGridTable();

  return (
    <div className={classNames(rowClassName, headRowClassName, className)} {...props}>
      {columns.map((column, colIndex) => {
        const { hidden } = column;

        if (hidden && hidden({ meta })) {
          return null;
        }

        return (
          <div
            className={classNames(
              'grid-table-cell',
              typeof cellClassName === 'function'
                ? cellClassName({ rowIndex: -1, colIndex })
                : cellClassName,
              headCellClassName,
              column.cellClassName,
              column.headCellClassName,
            )}
            key={column.name}
          >
            {column.label}
          </div>
        );
      })}
      {columns.some((column) => column.additionalContent) && (
        <div className={classNames('grid-table-cell', headCellClassName)} />
      )}
    </div>
  );
};

/**
 * @deprecated `GridTable` was deprecated. Please, use only new `Table` component
 */
export const GridTable = <Data extends GridTableData, Meta>({
  className,
  columns,
  data,
  children,
  rowClassName,
  cellClassName,
  bodyRowClassName,
  bodyCellClassName,
  headCellClassName,
  headRowClassName,
  rowId,
  onClickRow,
  BodyRowComponent = BaseGridTableBodyRow,
  HeadRowComponent = BaseGridTableHeadRow,
  meta,
  ...props
}: GridTableProps<Data, Meta>) => {
  const value = useMemo(() => {
    return {
      columns: columns as unknown as GridTableColumn<GridTableData, any>[],
      rowClassName: classNames('grid-table-row', rowClassName),
      cellClassName,
      bodyRowClassName: bodyRowClassName as any,
      bodyCellClassName,
      headCellClassName,
      headRowClassName,
      data,
      rowId: rowId as any,
      meta: meta as any,
      HeadRowComponent,
      BodyRowComponent,
    };
  }, [
    columns,
    data,
    rowClassName,
    rowId,
    cellClassName,
    bodyRowClassName,
    bodyCellClassName,
    headCellClassName,
    headRowClassName,
    HeadRowComponent,
    BodyRowComponent,
    meta,
  ]);

  return (
    <Context.Provider value={value}>
      <div className={classNames('grid-table', className)} {...props}>
        {HeadRowComponent !== null && <HeadRowComponent />}

        {BodyRowComponent !== null &&
          data?.map((dataItem, i) => {
            let key: string | number = i;

            if (typeof rowId === 'function') {
              key = rowId({ row: dataItem });
            } else if (typeof rowId === 'string') {
              key = dataItem[rowId];
            }

            return (
              <GridTableRowProvider meta={meta} key={key} rowIndex={i} row={dataItem}>
                <BodyRowComponent
                  className={onClickRow ? 'cursor-pointer' : ''}
                  onClick={() => (onClickRow ? onClickRow(dataItem as Data) : undefined)}
                />
              </GridTableRowProvider>
            );
          })}
        {children}
      </div>
    </Context.Provider>
  );
};
